import React, { createContext, useContext, useEffect } from 'react';
import { useStore } from '../store/useStore';
import type { ThemeType, ThemeTokens } from './index';
import { skeuomorphismTokens, skeuomorphismDarkTokens } from './skeuomorphism/tokens';
import { neubrutalsimTokens, neubrutalsimDarkTokens } from './neubrutalism/tokens';

interface ThemeContextValue {
  theme: ThemeType;
  tokens: ThemeTokens;
  setTheme: (theme: ThemeType) => void;
  isSkeuomorphism: boolean;
  isNeubrutalism: boolean;
  themeClass: string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

function getTokens(theme: ThemeType, darkMode: boolean): ThemeTokens {
  if (theme === 'skeuomorphism') {
    if (darkMode) {
      return { ...skeuomorphismTokens, ...skeuomorphismDarkTokens } as ThemeTokens;
    }
    return skeuomorphismTokens;
  }
  // neubrutalism
  if (darkMode) {
    return { ...neubrutalsimTokens, ...neubrutalsimDarkTokens } as ThemeTokens;
  }
  return neubrutalsimTokens;
}

function applyCssVariables(tokens: ThemeTokens) {
  const root = document.documentElement;
  
  // Surface colors
  root.style.setProperty('--theme-surface-primary', tokens.surface.primary);
  root.style.setProperty('--theme-surface-secondary', tokens.surface.secondary);
  root.style.setProperty('--theme-surface-tertiary', tokens.surface.tertiary);
  root.style.setProperty('--theme-surface-elevated', tokens.surface.elevated);
  root.style.setProperty('--theme-surface-sunken', tokens.surface.sunken);
  
  // Border
  root.style.setProperty('--theme-border-width', tokens.border.width);
  root.style.setProperty('--theme-border-radius', tokens.border.radius);
  root.style.setProperty('--theme-border-color', tokens.border.color);
  
  // Shadows
  root.style.setProperty('--theme-shadow-sm', tokens.shadow.sm);
  root.style.setProperty('--theme-shadow-md', tokens.shadow.md);
  root.style.setProperty('--theme-shadow-lg', tokens.shadow.lg);
  root.style.setProperty('--theme-shadow-inset', tokens.shadow.inset);
  
  // Typography
  root.style.setProperty('--theme-font-family', tokens.typography.fontFamily);
  root.style.setProperty('--theme-heading-weight', tokens.typography.headingWeight);
  root.style.setProperty('--theme-body-weight', tokens.typography.bodyWeight);
  root.style.setProperty('--theme-letter-spacing', tokens.typography.letterSpacing);
  
  // Card
  root.style.setProperty('--theme-card-bg', tokens.card.background);
  root.style.setProperty('--theme-card-border', tokens.card.border);
  root.style.setProperty('--theme-card-shadow', tokens.card.shadow);
  root.style.setProperty('--theme-card-padding', tokens.card.padding);
  root.style.setProperty('--theme-card-radius', tokens.card.radius);
  
  // Button
  root.style.setProperty('--theme-btn-primary', tokens.button.primary);
  root.style.setProperty('--theme-btn-secondary', tokens.button.secondary);
  root.style.setProperty('--theme-btn-border', tokens.button.border);
  root.style.setProperty('--theme-btn-shadow', tokens.button.shadow);
  root.style.setProperty('--theme-btn-radius', tokens.button.radius);
  
  // Theme class
  root.classList.remove('theme-skeuomorphism', 'theme-neubrutalism');
  root.classList.add(`theme-${tokens.name}`);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { uiTheme, setUiTheme, darkMode } = useStore();
  const tokens = getTokens(uiTheme, darkMode);
  
  useEffect(() => {
    applyCssVariables(tokens);
  }, [uiTheme, darkMode]);
  
  const themeClass = uiTheme === 'neubrutalism' ? 'theme-neubrutalism' : 'theme-skeuomorphism';
  
  const value: ThemeContextValue = {
    theme: uiTheme,
    tokens,
    setTheme: setUiTheme,
    isSkeuomorphism: uiTheme === 'skeuomorphism',
    isNeubrutalism: uiTheme === 'neubrutalism',
    themeClass,
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
